import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, PlayCircle, ShieldAlert, EyeOff, CheckCircle2, Gavel, Users, Clock, Activity, Save, RotateCcw } from "lucide-react";

const prettySection = (sec) => {
  if (!sec) return "";
  const map = {
    MENTAL_HEALTH: "Mental Health",
    BRAIN_HEALTH: "Brain Health",
    ADDICTIONS: "Addictions",
    COGNITIVE_HEALTH: "Brain Health",
    INTOXICATION: "Addictions",
  };
  if (map[sec]) return map[sec];
  return String(sec).replace(/_/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
};

const CONF_LOW = 0.7;
const CONF_HIGH = 0.9;
const confidenceTone = (c) => {
  if (c == null) return "bg-muted text-muted-foreground border-border/60";
  if (c >= CONF_HIGH) return "bg-primary/10 text-primary border-primary/30";
  if (c >= CONF_LOW) return "bg-secondary/40 text-primary border-secondary";
  return "bg-destructive/10 text-destructive border-destructive/40";
};

// Fetches an authenticated audio blob and plays it inline. We can't set
// Authorization headers on a bare <audio src=> tag, so we pull the bytes via
// axios, wrap them in an object URL and hand that to the native player.
function AuthedAudio({ sampleId }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(`/moca-voice/audio/${sampleId}`, { responseType: "blob" });
      setUrl(URL.createObjectURL(res.data));
    } catch (e) {
      setError(e?.response?.data?.detail || "Playback unavailable");
    } finally {
      setLoading(false);
    }
  }, [sampleId]);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  if (!url) {
    return (
      <button type="button" onClick={load} disabled={loading}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
              data-testid={`voice-moca-play-${sampleId}`}>
        <PlayCircle size={14} /> {loading ? "Loading…" : error ? "Retry playback" : "Load recording"}
      </button>
    );
  }
  return (
    <audio controls src={url} className="w-full h-8"
           data-testid={`voice-moca-audio-${sampleId}`} />
  );
}

// One row in the clinician voice-MoCA card. Shows the task transcript,
// confidence, LLM rationale, embedded audio player, and an inline override
// input that recomputes totals on save.
function VoiceMocaTaskRow({ task, responseId, onOverrideSaved }) {
  const rub = task.rubric || {};
  const overridden = !!rub.override;
  const effectiveScore = overridden ? rub.override.score : rub.score;
  const [editing, setEditing] = useState(false);
  const [score, setScore] = useState(String(effectiveScore ?? 0));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const max = rub.max ?? 1;

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/clinician/moca-voice/${responseId}/override`, {
        item_id: task.id,
        score: Number.parseInt(score, 10),
        note: note.trim() || null,
      });
      onOverrideSaved(res.data);
      setEditing(false);
      setNote("");
      toast.success("Score override saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save override");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border/60 p-3" data-testid={`voice-moca-task-${task.id}`}>
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <div className="text-sm font-medium capitalize">{task.id.replace(/_/g, " ")}</div>
        <div className="flex items-center gap-2 flex-wrap">
          {rub.confidence != null && (
            <Badge className={"border text-[10px] px-2 py-0.5 " + confidenceTone(rub.confidence)}
                   data-testid={`voice-moca-conf-${task.id}`}>
              {(rub.confidence * 100).toFixed(0)}% conf
            </Badge>
          )}
          <div className="text-xs text-muted-foreground" data-testid={`voice-moca-score-${task.id}`}>
            <span className={overridden ? "text-primary font-medium" : ""}>
              {effectiveScore != null ? `${effectiveScore}/${max}` : "—"}
            </span>
            {overridden && (
              <span className="ml-1 text-[10px] uppercase tracking-wider text-secondary-foreground"
                    data-testid={`voice-moca-overridden-${task.id}`}>
                overridden
              </span>
            )}
          </div>
        </div>
      </div>
      {task.target_text && (
        <div className="text-xs text-muted-foreground italic mb-1">Target: “{task.target_text}”</div>
      )}
      <div className="text-sm bg-muted/40 rounded p-2 whitespace-pre-wrap"
           data-testid={`voice-moca-transcript-${task.id}`}>
        {task.transcript || <span className="text-muted-foreground italic">(no transcript)</span>}
      </div>
      {rub.rationale && (
        <div className="text-xs text-muted-foreground mt-1">
          <span className="font-medium">Rubric:</span> {rub.rationale}
        </div>
      )}
      {overridden && rub.override?.note && (
        <div className="text-xs text-secondary-foreground mt-1">
          <span className="font-medium">Override note:</span> {rub.override.note}
        </div>
      )}
      {task.sample_id && (
        <div className="mt-2">
          <AuthedAudio sampleId={task.sample_id} />
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!editing ? (
          <Button size="sm" variant="outline" className="rounded-full h-8"
                  onClick={() => { setEditing(true); setScore(String(effectiveScore ?? 0)); }}
                  data-testid={`voice-moca-edit-${task.id}`}>
            {overridden ? <><RotateCcw size={12} /> Adjust override</> : <>Override</>}
          </Button>
        ) : (
          <>
            <Input type="number" min={0} max={max} step={1}
                   value={score} onChange={(e) => setScore(e.target.value)}
                   className="w-20 h-8"
                   data-testid={`voice-moca-score-input-${task.id}`} />
            <span className="text-xs text-muted-foreground">/ {max}</span>
            <Input placeholder="Note (optional)"
                   value={note} onChange={(e) => setNote(e.target.value)}
                   className="flex-1 min-w-[160px] h-8 text-sm"
                   data-testid={`voice-moca-note-input-${task.id}`} />
            <Button size="sm" onClick={save} disabled={saving}
                    className="rounded-full h-8"
                    data-testid={`voice-moca-save-${task.id}`}>
              <Save size={12} /> {saving ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}
                    disabled={saving} className="h-8 text-xs"
                    data-testid={`voice-moca-cancel-${task.id}`}>
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

const BAND_TONE = {
  Normal: "bg-primary/10 text-primary border-primary/30",
  Mild: "bg-secondary/30 text-primary border-secondary",
  Moderate: "bg-[hsl(var(--chart-4)/0.25)] text-foreground border-[hsl(var(--chart-4))]",
  High: "bg-destructive/10 text-destructive border-destructive/40",
};

const TREMOR_TONE = {
  normal: "bg-primary/10 text-primary border-primary/30",
  elevated: "bg-secondary/40 text-primary border-secondary",
  high: "bg-destructive/10 text-destructive border-destructive/40",
  unavailable: "bg-muted/40 text-muted-foreground border-border/60",
};

function VoiceTremorBadge({ analysis }) {
  if (!analysis) return null;
  const flag = analysis.tremor_flag || "unavailable";
  const label = flag === "normal" ? "Voice: normal"
              : flag === "elevated" ? "Voice: elevated"
              : flag === "high" ? "Voice: high dysphonia"
              : "Voice analysis unavailable";
  return (
    <div className="mt-3 rounded-md border p-3 space-y-2"
         data-testid={`voice-tremor-${analysis.tremor_flag || "unavailable"}`}>
      <div className="flex items-center gap-2">
        <Activity size={14} className="text-primary" />
        <Badge className={"border " + (TREMOR_TONE[flag] || TREMOR_TONE.unavailable)}>
          {label}
        </Badge>
      </div>
      {flag !== "unavailable" && (
        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <div>
            <div className="uppercase tracking-wider text-muted-foreground">Jitter</div>
            <div className="font-display">{analysis.jitter_local_pct ?? "—"}<span className="text-muted-foreground">%</span></div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-muted-foreground">Shimmer</div>
            <div className="font-display">{analysis.shimmer_local_pct ?? "—"}<span className="text-muted-foreground">%</span></div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-muted-foreground">HNR</div>
            <div className="font-display">{analysis.hnr_db ?? "—"}<span className="text-muted-foreground"> dB</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function VoicePlayer({ sample }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("mano_jwt");
      const res = await fetch(`${API}/clinician/voice/${sample.sample_id}/download`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      setUrl(URL.createObjectURL(blob));
    } catch {
      toast.error("Could not load voice sample");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  return (
    <div className="rounded-lg border border-border/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm">
          <span className="font-medium">{sample.prompt_id}</span>
          <span className="text-muted-foreground ml-2">· {Math.round(sample.duration_seconds)}s</span>
        </div>
        {!url && (
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            className="rounded-full" data-testid={`voice-load-${sample.sample_id.slice(0,8)}`}>
            <PlayCircle size={14} /> {loading ? "Loading…" : "Load audio"}
          </Button>
        )}
      </div>
      {url && (
        <audio controls src={url} className="w-full" data-testid={`voice-audio-${sample.sample_id.slice(0,8)}`} />
      )}
      <VoiceTremorBadge analysis={sample.voice_analysis} />
    </div>
  );
}

export default function ClinicianCaseDetail() {
  const { case_id } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/clinician/cases/${case_id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load case"));
    api.get(`/clinician/cases/${case_id}/timeline`)
      .then((r) => setTimeline(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/admin/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, case_id]);

  const submitReview = async () => {
    if (!outcome) { toast.error("Please select an outcome"); return; }
    if (notes.trim().length < 20) { toast.error("Please provide notes (min 20 characters)"); return; }
    setSubmitting(true);
    try {
      await api.post(`/clinician/cases/${case_id}/review`, { outcome, notes });
      toast.success("Review submitted");
      setOutcome(""); setNotes("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container py-20 text-muted-foreground">Loading case…</div>
      </div>
    );
  }

  const { case: c, respondent, scores_grouped, voice_samples, voice_moca, my_review, other_reviews, reveal, outcomes, tie_breaker_enabled } = data;
  const sections = Object.keys(scores_grouped || {});
  const bothSubmitted = (other_reviews?.length || 0) + (my_review ? 1 : 0) >= 2;
  const outcomesMatch = bothSubmitted && my_review && other_reviews[0] && my_review.outcome === other_reviews[0].outcome;

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container py-8">
        <button
          onClick={() => nav("/clinician")}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          data-testid="case-back-btn"
        >
          <ArrowLeft size={14} /> Back to queue
        </button>

        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="font-mono text-[10px]" data-testid="case-detail-id">
                {c.case_id.slice(0, 8)}
              </Badge>
              {c.high_severity && (
                <Badge className="bg-destructive/10 text-destructive border border-destructive/30">
                  <ShieldAlert size={12} /> High severity
                </Badge>
              )}
              <Badge variant="outline">Status: {c.status.replace(/_/g, " ")}</Badge>
            </div>
            <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-3">
              {respondent?.name || "Respondent"}
            </h1>
            <p className="text-muted-foreground">{respondent?.email} · {respondent?.profession}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column — case package */}
          <div className="lg:col-span-2 space-y-6">
            {/* Respondent context */}
            <Card className="mano-card">
              <CardContent className="p-6">
                <h3 className="font-display font-semibold text-lg mb-3">Respondent context</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">DOB:</span> {respondent?.dob || "-"}</div>
                  <div><span className="text-muted-foreground">Gender:</span> {respondent?.gender || "-"}</div>
                  <div><span className="text-muted-foreground">Language:</span> {respondent?.preferred_language || "-"}</div>
                  <div><span className="text-muted-foreground">Contact:</span> {respondent?.contact || "-"}</div>
                </div>
              </CardContent>
            </Card>

            {/* Stage 1: Voice */}
            <Card className="mano-card">
              <CardContent className="p-6">
                <h3 className="font-display font-semibold text-lg mb-4">Stage 1 · Voice samples</h3>
                {voice_samples.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No voice samples on record.</div>
                ) : (
                  <div className="space-y-3">
                    {voice_samples.map((s) => <VoicePlayer key={s.sample_id} sample={s} />)}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stage 2: Scores */}
            <Card className="mano-card">
              <CardContent className="p-6">
                <h3 className="font-display font-semibold text-lg mb-4">Stage 2 · Questionnaire results</h3>
                {sections.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No responses yet.</div>
                ) : (
                  sections.map((section) => (
                    <div key={section} className="mb-5">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        {prettySection(section)}
                      </div>
                      <div className="grid gap-2">
                        {scores_grouped[section].map((r) => (
                          <div key={r.instrument_id} className="rounded-lg border border-border/60 p-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium">{r.title}</div>
                                <div className="text-xs text-muted-foreground">{r.label}</div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge className={"border " + (BAND_TONE[r.band] || "")} data-testid={`case-band-${r.instrument_id}`}>
                                  {r.band}
                                </Badge>
                                <div className="text-right">
                                  <div className="text-[10px] text-muted-foreground">Score</div>
                                  <div className="font-display text-lg leading-none" data-testid={`case-total-${r.instrument_id}`}>{r.total}</div>
                                </div>
                              </div>
                            </div>
                            {r.domain_scores && r.domain_scores.length > 0 && (
                              <div
                                className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-border/50"
                                data-testid={`case-domains-${r.instrument_id}`}
                              >
                                {r.domain_scores.map((d) => (
                                  <div key={d.key} className="rounded-md bg-muted/40 px-2 py-1.5"
                                       data-testid={`case-domain-${r.instrument_id}-${d.key}`}>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{d.label}</div>
                                    <div className="font-display text-sm leading-tight mt-0.5">
                                      {d.score}
                                      {d.max != null && <span className="text-xs text-muted-foreground">/{d.max}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Voice-MoCA: transcripts + LLM rubric rationale + audio + overrides */}
            {voice_moca && voice_moca.length > 0 && (
              <Card className="mano-card" data-testid="case-voice-moca">
                <CardContent className="p-6">
                  <h3 className="font-display font-semibold text-lg mb-1">
                    Voice-MoCA · transcripts, audio &amp; scoring
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    Play each recording, review the LLM's transcript, confidence and
                    rationale, and override the score for any item — domain totals and
                    the band recompute automatically.
                  </p>
                  <div className="space-y-6">
                    {voice_moca.map((vm, i) => (
                      <div key={vm.response_id || i} className="space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            Submitted {(vm.created_at || "").replace("T", " ").slice(0, 16)}
                            {vm.updated_at && (
                              <> · Updated {(vm.updated_at || "").replace("T", " ").slice(0, 16)}</>
                            )}
                          </div>
                          {vm.score && (
                            <div className="flex items-center gap-2">
                              <Badge className={"border text-xs " + (BAND_TONE[vm.score.band] || "")}
                                     data-testid={`voice-moca-band-${vm.response_id}`}>
                                {vm.score.band}
                              </Badge>
                              <div className="font-display text-sm"
                                   data-testid={`voice-moca-total-${vm.response_id}`}>
                                {vm.score.total}<span className="text-muted-foreground"> / {vm.score.total_max || 30}</span>
                              </div>
                              {vm.score.has_clinician_overrides && (
                                <Badge variant="outline" className="text-[10px]"
                                       data-testid={`voice-moca-has-overrides-${vm.response_id}`}>
                                  clinician overrides
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {(vm.tasks || []).filter((t) => t.scored !== false).map((t) => (
                            <VoiceMocaTaskRow
                              key={t.id}
                              task={t}
                              responseId={vm.response_id}
                              onOverrideSaved={(updated) => {
                                // Merge updated tasks + score back into the local `data` state.
                                setData((prev) => {
                                  if (!prev) return prev;
                                  const nextVoiceMoca = (prev.voice_moca || []).map((entry) => {
                                    if (entry.response_id !== vm.response_id) return entry;
                                    // Also refresh override_log with the new entries so the
                                    // audit trail panel below reflects the change immediately.
                                    const nextLog = [];
                                    for (const ut of (updated.tasks || [])) {
                                      const hist = ut.rubric?.override_history || [];
                                      for (const ov of hist) {
                                        nextLog.push({ ...ov, item_id: ov.item_id || ut.id });
                                      }
                                    }
                                    nextLog.sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || "")));
                                    return {
                                      ...entry,
                                      score: updated.score,
                                      tasks: updated.tasks,
                                      override_log: nextLog,
                                      updated_at: new Date().toISOString(),
                                    };
                                  });
                                  return { ...prev, voice_moca: nextVoiceMoca };
                                });
                              }}
                            />
                          ))}
                        </div>
                        {(vm.override_log || []).length > 0 && (
                          <div className="mt-3 rounded-lg border border-border/60 bg-muted/20 p-3"
                               data-testid={`voice-moca-override-log-${vm.response_id}`}>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                              Override audit trail · {vm.override_log.length} change{vm.override_log.length === 1 ? "" : "s"}
                            </div>
                            <div className="space-y-1">
                              {vm.override_log.map((ov, ix) => (
                                <div key={ix}
                                     className="text-xs flex flex-wrap items-center gap-x-2 gap-y-0.5"
                                     data-testid={`voice-moca-override-entry-${ix}`}>
                                  <span className="font-mono text-[10px] text-muted-foreground">
                                    {(ov.timestamp || "").replace("T", " ").slice(0, 16)}
                                  </span>
                                  <span className="capitalize font-medium">
                                    {String(ov.item_id || "").replace(/_/g, " ")}
                                  </span>
                                  <span className="text-muted-foreground">·</span>
                                  <span className="text-muted-foreground">
                                    {(ov.prior_override?.score ?? ov.prior_llm_score) ?? "-"} →{" "}
                                  </span>
                                  <span className="font-medium text-primary">{ov.score}</span>
                                  <span className="text-muted-foreground">·</span>
                                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                    {ov.actor_role}
                                  </span>
                                  {ov.note && (
                                    <span className="basis-full text-muted-foreground italic">"{ov.note}"</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right column — review */}
          <div className="space-y-6">
            {my_review ? (
              <Card className="mano-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-primary">
                    <CheckCircle2 size={18} /> <span className="font-medium">Your review is on record</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Submitted {(my_review.submitted_at || "").replace("T", " ").slice(0, 16)}</div>
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Outcome</div>
                    <div className="mt-1 font-medium">{outcomes.find((o) => o.key === my_review.outcome)?.label || my_review.outcome}</div>
                  </div>
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Notes</div>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{my_review.notes}</div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="mano-card">
                <CardContent className="p-6">
                  <h3 className="font-display font-semibold text-lg mb-1">Submit your independent review</h3>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <EyeOff size={12} /> Blind — the other reviewer's notes are hidden until both submit.
                  </div>

                  <div className="mt-5">
                    <Label className="text-sm">Outcome</Label>
                    <RadioGroup value={outcome} onValueChange={setOutcome} className="mt-2 grid gap-2">
                      {outcomes.map((o) => (
                        <label key={o.key} htmlFor={`o-${o.key}`}
                          className={"flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors duration-150 " +
                            (outcome === o.key ? "border-primary bg-secondary/25" : "border-border hover:bg-muted/50")}
                          data-testid={`review-outcome-${o.key}`}>
                          <RadioGroupItem id={`o-${o.key}`} value={o.key} />
                          <span className="text-sm">{o.label}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="mt-5">
                    <Label className="text-sm">Clinical notes (min 20 chars)</Label>
                    <Textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Provide reasoning, observations from voice + questionnaire data…"
                      className="mt-2" data-testid="review-notes" />
                  </div>

                  <Button onClick={submitReview} disabled={submitting}
                    className="rounded-full mt-6 w-full mano-lift" data-testid="review-submit-btn">
                    {submitting ? "Submitting…" : "Submit review"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Reconciliation */}
            {my_review && !reveal && (
              <Card className="mano-card">
                <CardContent className="p-6 text-sm text-muted-foreground flex items-start gap-3">
                  <EyeOff className="text-muted-foreground shrink-0 mt-0.5" size={16} />
                  Waiting for the other clinician's review. You'll see their outcome and notes here once submitted.
                </CardContent>
              </Card>
            )}

            {reveal && (
              <Card className="mano-card">
                <CardContent className="p-6">
                  <h3 className="font-display font-semibold text-lg mb-3 flex items-center gap-2">
                    <Users size={16} /> Reconciliation
                  </h3>

                  {c.status === "finalized" && (
                    <div className={"rounded-lg border p-3 mb-4 " + (outcomesMatch ? "border-primary/30 bg-primary/5" : "border-[hsl(var(--chart-4))] bg-[hsl(var(--chart-4)/0.15)]")}>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        {outcomesMatch ? <CheckCircle2 size={16} className="text-primary" /> : <Gavel size={16} />}
                        {outcomesMatch ? "Outcomes matched — finalized" : "Reconciled via psychiatrist tie-breaker"}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Final outcome: <span className="font-medium text-foreground">
                          {outcomes.find((o) => o.key === c.final_outcome)?.label || c.final_outcome}
                        </span>
                      </div>
                    </div>
                  )}

                  {c.status === "tie_break_needed" && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 mb-4 text-sm">
                      <Gavel size={14} className="inline mr-1 text-destructive" />
                      Outcomes differ and psychiatrist tie-breaker is disabled. Awaiting admin resolution.
                    </div>
                  )}

                  <div className="grid gap-3">
                    {[my_review, ...(other_reviews || [])].filter(Boolean).map((r) => (
                      <div key={r.review_id} className="rounded-lg border border-border/60 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-sm font-medium">
                            {r.reviewer_name}
                            <Badge variant="outline" className="ml-2 text-[10px]">{r.reviewer_role}</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</div>
                        </div>
                        <div className="text-sm mt-1">
                          <span className="text-muted-foreground">Outcome:</span>{" "}
                          <span className="font-medium">{outcomes.find((o) => o.key === r.outcome)?.label || r.outcome}</span>
                        </div>
                        <div className="text-sm mt-2 whitespace-pre-wrap">{r.notes}</div>
                      </div>
                    ))}
                  </div>
                  {!tie_breaker_enabled && (
                    <div className="text-xs text-muted-foreground mt-4">
                      Note: psychiatrist tie-breaker is currently disabled by admin.
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card className="mano-card">
              <CardContent className="p-6">
                <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2">
                  <Clock size={16} /> Case timeline
                </h3>
                {timeline.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No events yet.</div>
                ) : (
                  <ol className="relative border-l border-border/60 pl-4 space-y-4" data-testid="case-timeline">
                    {timeline.map((ev) => (
                      <li key={ev.event_id} className="relative">
                        <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                        <div className="text-xs text-muted-foreground">
                          {(ev.timestamp || "").replace("T", " ").slice(0, 19)}
                        </div>
                        <div className="text-sm font-medium mt-0.5">
                          {ev.event_type.replace(/\./g, " · ")}
                          <Badge variant="outline" className="ml-2 text-[10px]">{ev.actor_role}</Badge>
                        </div>
                        {ev.meta && Object.keys(ev.meta).length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                            {Object.entries(ev.meta).map(([k, v]) => (
                              <span key={k}>
                                <span className="opacity-70">{k}:</span>{" "}
                                <span className="text-foreground">{v === null ? "—" : String(v).slice(0, 40)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
